/**
 * Recipient check (John Garcia, 3 Nov 2026) — deals whose Contact email is an automated address (no-reply /
 * notifications…). Held in Processing at Application stage, or flagged (chases paused) further down the board.
 * The suggested real recipient is applied with one click; a different address can be typed instead.
 */
import { useState, useEffect, useCallback } from "react";
import { MailWarning, Check, X, Loader2, ExternalLink, RefreshCw, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { mirrorRecipientList, mirrorRecipientApply, mirrorRecipientDismiss, mirrorRecipientRun, mirrorRecipientForgetMapping } from "@/lib/api";

const err = (e, fb) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : fb; };

/** Learned platform mappings — a pair fixed `autoAfter` times is applied on arrival by Autopilot. */
function LearnedMappings({ mappings, autoAfter, onChanged }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(null);
  if (!mappings?.length) return null;
  const toggle = async (m) => {
    setBusy(m.bad_email + m.fixed_email);
    try { await mirrorRecipientForgetMapping(m.bad_email, m.fixed_email, user?.name || "Ops", !!m.disabled); toast.success(m.disabled ? "Mapping restored" : "Mapping switched off"); onChanged?.(); }
    catch (e) { toast.error(err(e, "Could not update the mapping")); }
    finally { setBusy(null); }
  };
  return (
    <details className="text-[11px] border rounded-lg p-2 bg-slate-50" data-testid="recipient-mappings" open>
      <summary className="cursor-pointer font-medium text-slate-700">Learned platforms ({mappings.length}) — auto-applied on arrival after {autoAfter} fixes</summary>
      <ul className="mt-1.5 space-y-1">
        {mappings.map((m) => (
          <li key={m.bad_email + m.fixed_email} className="flex items-center gap-2 flex-wrap" data-testid={`recipient-mapping-${m.bad_email}`}>
            <code className="text-rose-700">{m.bad_email}</code> <span className="text-slate-400">→</span> <code className="text-emerald-700">{m.fixed_email}</code>
            <span className="text-slate-500">fixed {m.count}×{m.auto_applied ? `, auto-applied ${m.auto_applied}×` : ""}</span>
            <Badge variant="outline" className={`text-[9px] ${m.disabled ? "text-slate-500" : m.auto ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "text-amber-700 border-amber-300"}`}>
              {m.disabled ? "off" : m.auto ? "auto" : `${autoAfter - m.count} more fix${autoAfter - m.count === 1 ? "" : "es"} to auto`}
            </Badge>
            <button type="button" className="underline text-slate-500 ml-auto" disabled={busy === m.bad_email + m.fixed_email} onClick={() => toggle(m)}
              data-testid={`recipient-mapping-toggle-${m.bad_email}`}>{m.disabled ? "restore" : "switch off"}</button>
          </li>
        ))}
      </ul>
    </details>
  );
}

/** The suggestion card + Apply / type-your-own / Dismiss controls for one held or flagged deal. */
export function RecipientFix({ itemId, badEmail, suggestion, status, onDone, compact = false }) {
  const { user } = useAuth();
  const actor = user?.name || "Ops";
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(null);
  const sug = suggestion?.email || "";

  const apply = async (email, key) => {
    setBusy(key);
    try {
      const r = await mirrorRecipientApply(itemId, email, actor);
      toast.success(r.message || "Recipient fixed");
      if (r.remaining?.length) toast.message("Still held", { description: r.remaining.join("; ") });
      if (r.learned?.auto_from_now) toast.message("Platform learned", { description: `${badEmail} → ${email} has now been fixed ${r.learned.count}× — Autopilot applies it on arrival from now on.` });
      onDone?.(r);
    } catch (e) { toast.error(err(e, "Could not apply the recipient")); }
    finally { setBusy(null); }
  };
  const dismiss = async (whitelist) => {
    setBusy("dismiss");
    try { await mirrorRecipientDismiss(itemId, actor, whitelist); toast.success(whitelist ? "Address whitelisted" : "Dismissed for this deal"); onDone?.(); }
    catch (e) { toast.error(err(e, "Could not dismiss")); }
    finally { setBusy(null); }
  };

  return (
    <div className={`rounded-md border border-amber-200 bg-amber-50/60 ${compact ? "p-2" : "p-2.5"} space-y-1.5`} data-testid={`recipient-fix-${itemId}`}>
      <div className="flex items-center gap-2 flex-wrap text-[11px]">
        <span className="text-slate-500">Contact email</span>
        <code className="rounded bg-rose-50 px-1 text-rose-700 border border-rose-200" data-testid={`recipient-bad-${itemId}`}>{badEmail}</code>
        <Badge variant="outline" className="text-[9px] bg-amber-100 text-amber-800 border-amber-300">{status === "held" ? "held in Processing" : "flagged · chases paused"}</Badge>
      </div>
      {sug ? (
        <div className="flex items-center gap-2 flex-wrap">
          <div className="min-w-0">
            <div className="text-[11px] text-slate-500">Suggested recipient</div>
            <div className="text-xs font-semibold text-slate-800" data-testid={`recipient-suggested-${itemId}`}>{sug}</div>
            <div className="text-[10px] text-slate-500" data-testid={`recipient-source-${itemId}`}>from {suggestion.source}</div>
          </div>
          <Button size="sm" className="h-7 px-2.5 text-[11px] bg-amber-600 hover:bg-amber-700 ml-auto" disabled={!!busy}
            onClick={() => apply(sug, "apply")} data-testid={`recipient-apply-${itemId}`}>
            {busy === "apply" ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Check className="w-3 h-3 mr-1" /> Apply</>}
          </Button>
        </div>
      ) : (
        <div className="text-[11px] text-amber-800" data-testid={`recipient-none-${itemId}`}>No real recipient found in the original email, the Broker Database or earlier deals — type one below.</div>
      )}
      {(suggestion?.candidates || []).length > 1 && (
        <div className="text-[10px] text-slate-500 flex flex-wrap gap-1 items-center">
          <span>Also seen:</span>
          {suggestion.candidates.slice(1, 4).map((c) => (
            <button key={c.email} type="button" className="underline text-slate-600 hover:text-slate-900" title={c.source}
              onClick={() => setCustom(c.email)} data-testid={`recipient-candidate-${itemId}-${c.email}`}>{c.email}</button>
          ))}
        </div>
      )}
      <div className="flex items-center gap-1.5">
        <Input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="or type a different address…"
          className="h-7 text-[11px]" data-testid={`recipient-custom-${itemId}`} />
        <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" disabled={!!busy || !custom.trim()}
          onClick={() => apply(custom.trim(), "custom")} data-testid={`recipient-save-${itemId}`}>
          {busy === "custom" ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
        </Button>
        <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px] text-slate-500" disabled={!!busy}
          onClick={() => dismiss(false)} title="The address is fine for this deal" data-testid={`recipient-dismiss-${itemId}`}>
          <X className="w-3 h-3 mr-1" /> Dismiss
        </Button>
        <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px] text-slate-500" disabled={!!busy}
          onClick={() => dismiss(true)} title="This address is a real inbox — never flag it again" data-testid={`recipient-whitelist-${itemId}`}>
          Whitelist
        </Button>
      </div>
    </div>
  );
}

/** The full list — used on the Autopilot dialog's "Recipient check" tab. */
export function RecipientCheckTab({ onOpenItem, onChanged }) {
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);
  const load = useCallback(async () => {
    try { setData(await mirrorRecipientList()); } catch { toast.error("Failed to load the recipient check list"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setRunning(true);
    try {
      const r = await mirrorRecipientRun();
      toast.success(`Checked the board — ${r.holds_proposed} hold${r.holds_proposed === 1 ? "" : "s"} proposed, ${r.flagged} newly flagged, ${r.released} released`);
      await load(); onChanged?.();
    } catch (e) { toast.error(err(e, "Sweep failed")); }
    finally { setRunning(false); }
  };

  if (!data) return <Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto my-6" />;
  return (
    <div className="space-y-3" data-testid="recipient-check-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[11px] text-slate-600">
          <span className="font-semibold text-slate-800">{data.held}</span> held · <span className="font-semibold text-slate-800">{data.flagged}</span> flagged
          <span className="text-slate-400"> · patterns: {data.patterns.join(", ")}</span>
        </div>
        <div className="flex gap-1.5">
          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={load} data-testid="recipient-refresh"><RefreshCw className="w-3 h-3 mr-1" /> Refresh</Button>
          <Button size="sm" className="h-7 text-[11px]" onClick={run} disabled={running} data-testid="recipient-run">
            {running ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Play className="w-3 h-3 mr-1" />} Check the board now
          </Button>
        </div>
      </div>
      {data.active.length === 0 && (
        <div className="text-xs text-center py-6 text-emerald-700" data-testid="recipient-empty"><Check className="w-5 h-5 mx-auto mb-1" /> No deal has an automated contact address right now.</div>
      )}
      {data.active.map((r) => (
        <div key={r.hold_id} className="border rounded-lg p-2.5 bg-white space-y-1.5" data-testid={`recipient-row-${r.item_id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <MailWarning className="w-3.5 h-3.5 text-amber-600" />
            <span className="text-xs font-semibold">{r.sf_number || "no SF#"}</span>
            <span className="text-xs text-slate-700 truncate">{r.item_name}</span>
            {r.broker && <Badge variant="outline" className="text-[10px]">{r.broker}</Badge>}
            <Badge variant="outline" className="text-[10px]">{r.group_title} · {r.board_status}</Badge>
            <span className="text-[10px] text-slate-400 ml-auto" data-testid={`recipient-waiting-${r.item_id}`}>waiting {r.waiting}</span>
            {onOpenItem && (
              <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={() => onOpenItem(r.item_id)} data-testid={`recipient-open-${r.item_id}`}>
                <ExternalLink className="w-3 h-3 mr-1" /> Open
              </Button>
            )}
          </div>
          {r.risk && <div className="text-[11px] text-rose-700">{r.risk}</div>}
          {r.remaining?.length > 0 && <div className="text-[11px] text-amber-800">Recipient fixed ({r.fixed_email}) — still held: {r.remaining.join("; ")}</div>}
          <RecipientFix itemId={r.item_id} badEmail={r.bad_email} suggestion={r.suggestion} status={r.status} compact
            onDone={() => { load(); onChanged?.(); }} />
        </div>
      ))}
      <LearnedMappings mappings={data.mappings} autoAfter={data.auto_after} onChanged={load} />
      {data.recent?.length > 0 && (
        <details className="text-[11px] text-slate-500">
          <summary className="cursor-pointer">Recently fixed / released / auto-corrected ({data.recent.length})</summary>
          <ul className="mt-1 space-y-0.5">
            {data.recent.map((r) => (
              <li key={r.hold_id} data-testid={`recipient-recent-${r.item_id}`}>
                {r.sf_number || r.item_name}: {r.bad_email} → {r.fixed_email || "—"} · {r.status === "auto_applied" ? "auto-corrected on arrival" : r.status}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
